import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import paymentTypeService, { PaymentType } from '../../../../services/paymentTypeService';
import '../../../../pages/Products/ProductCategoryDetailPage.css';
import './PaymentTypeFormPage.css';
import { ArrowLeft, PenLine, Trash2 } from 'lucide-react';

const CATEGORY_MAP: Record<string, { icon: string; cls: string }> = {
  Cash:   { icon: '💵', cls: 'pt-cat-cash'   },
  Credit: { icon: '🏦', cls: 'pt-cat-credit' },
  Other:  { icon: '💳', cls: 'pt-cat-other'  },
};

const PaymentTypeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [item, setItem] = useState<PaymentType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(
    (location.state as any)?.message || null
  );

  useEffect(() => { loadItem(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const loadItem = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await paymentTypeService.getPaymentTypeById(parseInt(id || '0'));
      setItem(data);
    } catch {
      setError(t('paymentType.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="detail-container">
          <div className="alert alert-danger">{error || t('paymentType.notFound')}</div>
        </div>
      </Layout>
    );
  }

  const locale = i18n.language === 'ja' ? 'ja-JP' : 'en-US';
  const catCfg = CATEGORY_MAP[item.paymentCategory ?? 'Other'] ?? CATEGORY_MAP['Other'];

  return (
    <Layout>
      <div className="detail-container">
        {/* Navigation Breadcrumb */}
        <nav className="detail-breadcrumb">
          <button
            className="detail-breadcrumb-btn"
            onClick={() => navigate('/settings/payment-types')}
            title={t('paymentType.title')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('paymentType.title')}
          </button>
        </nav>

        {/* Page Title */}
        <div className="detail-header">
          <h1 className="detail-title">{item.name}</h1>
          <p className="detail-subtitle">{t('paymentType.details')}</p>
        </div>

        {/* Success Alert */}
        {successMsg && (
          <div className="alert" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', marginBottom: '20px', padding: '12px 16px', borderRadius: '6px', fontSize: '14px' }}>
            {successMsg}
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
          </div>
        )}

        <div className="detail-content">
          {/* Details Section */}
          <div className="detail-main-section">

            {/* Payment Type Details Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('paymentType.details')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">{t('paymentType.code')}</label>
                    <p className="detail-value detail-code">{item.code}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('paymentType.name')}</label>
                    <p className="detail-value">{item.name}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('paymentType.usageScope', 'Usage Scope')}</label>
                    <div className="detail-value">
                      <span className="pt-cat-badge pt-cat-badge-other">
                        { item.usageScope === 'Sales' ? '🛒'
                        : item.usageScope === 'Purchase' ? '📦'
                        : '🔄' }{' '}
                        {item.usageScope ?? 'Both'}
                      </span>
                    </div>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('paymentType.paymentCategory')}</label>
                    <div className="detail-value">
                      <span className={`pt-cat-badge ${catCfg.cls}`}>
                        {catCfg.icon} {item.paymentCategory ?? 'Other'}
                      </span>
                    </div>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.status')}</label>
                    <div className="detail-value">
                      {item.isActive ? (
                        <span className="info-badge info-badge-active">{t('common.active')}</span>
                      ) : (
                        <span className="info-badge info-badge-inactive">{t('common.inactive')}</span>
                      )}
                    </div>
                  </div>

                  <div className="detail-field detail-field-full">
                    <label className="detail-label">{t('common.description')}</label>
                    <p className="detail-value detail-description">
                      {item.description || <em className="detail-empty">—</em>}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Information Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('common.info')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">{t('paymentType.id') || 'ID'}</label>
                    <p className="detail-value detail-code">{item.id}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('paymentType.sortOrder')}</label>
                    <p className="detail-value">{item.sortOrder}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('paymentType.isDefault')}</label>
                    <div className="detail-value">
                      {item.isDefault ? (
                        <span className="info-badge info-badge-sub">✓ {t('paymentType.default')}</span>
                      ) : (
                        <em className="detail-empty">—</em>
                      )}
                    </div>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.createdDate')}</label>
                    <p className="detail-value">
                      {new Date(item.createdDate).toLocaleDateString(locale)}
                    </p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.createdTime') || 'Created Time'}</label>
                    <p className="detail-value">
                      {new Date(item.createdDate).toLocaleTimeString(locale)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Actions Sidebar */}
          <div className="detail-sidebar">

            {/* Actions Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('common.quickActions')}</h3>
              </div>
              <div className="detail-card-body detail-actions">
                <button
                  className="btn-save"
                  onClick={() => navigate(`/settings/payment-types/${id}/edit`)}
                  title={t('common.edit')}
                >
                  <PenLine size={16} strokeWidth={2} />
                  {t('common.edit')}
                </button>

                <button
                  className="btn-delete"
                  onClick={() => navigate(`/settings/payment-types/${id}/delete`)}
                  title={t('common.delete')}
                >
                  <Trash2 size={16} strokeWidth={2} />
                  {t('common.delete')}
                </button>

                <div className="detail-divider"></div>

                <button
                  className="detail-btn detail-btn-secondary"
                  onClick={() => navigate('/settings/payment-types')}
                  title={t('paymentType.backToList')}
                >
                  <ArrowLeft size={16} strokeWidth={2} />
                  {t('paymentType.backToList')}
                </button>
              </div>
            </div>

            {/* Quick Links Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('paymentType.detailInfo') || 'About Payment Types'}</h3>
              </div>
              <div className="detail-card-body">
                <button
                  className="detail-quick-link"
                  onClick={() => navigate(`/settings/payment-types/${id}/edit`)}
                >
                  <span className="detail-quick-icon">→</span>
                  <span>{t('common.edit')}</span>
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/settings/payment-types/create')}
                >
                  <span className="detail-quick-icon">→</span>
                  <span>{t('paymentType.createTitle')}</span>
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/settings/payment-types')}
                >
                  <span className="detail-quick-icon">→</span>
                  <span>{t('paymentType.backToList')}</span>
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentTypeDetailPage;
